import { useState, useEffect, useCallback, useRef } from 'react'

// Default debounce delay for onChange (ms)
const DEFAULT_DEBOUNCE_MS = 300

interface UseAutoSaveFieldOptions {
  /** Enable auto-resize for textareas */
  autoResize?: boolean
  /** Minimum height in pixels for auto-resize (default: 26) */
  minHeight?: number
  /** Node ID for auto-focus matching */
  nodeId?: string
  /** Auto-edit node ID from context */
  autoEditNodeId?: string
  /** Callback to clear auto-edit state */
  clearAutoEdit?: () => void
  /** Debounce delay for onChange in ms (default: 300) */
  debounceMs?: number
  /** When true, disables editing: handlers become no-ops, auto-focus is skipped */
  readOnly?: boolean
}

interface UseAutoSaveFieldReturn<T> {
  /** Current value - reads from DOM if focused, otherwise from external */
  value: T
  /** Whether the field is currently focused */
  isFocused: boolean
  /** Ref to attach to input/textarea element */
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>
  /** Handle input change events */
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** Handle focus events */
  handleFocus: () => void
  /** Handle blur events */
  handleBlur: () => void
  /** Manually set the value (updates DOM directly) */
  setValue: (value: T) => void
  /** Manually trigger textarea resize */
  triggerResize: () => void
  /** Whether the value is empty (trimmed) */
  isEmpty: boolean
}

/**
 * Hook for text inputs/textareas that provides smooth typing experience.
 *
 * Key design: During typing, the DOM manages the textarea value directly.
 * No React state updates during typing = no re-renders = no cursor jumping.
 *
 * Features:
 * - Zero React re-renders during typing (DOM manages value)
 * - Debounced onChange to update parent state
 * - Syncs from external value when not focused (for AI updates, undo/redo)
 * - Optional auto-resize for textareas
 * - Auto-focuses new nodes if requested
 */
export function useAutoSaveField<T extends string | string[]>(
  externalValue: T | undefined,
  onChange: (value: T) => void,
  options: UseAutoSaveFieldOptions = {}
): UseAutoSaveFieldReturn<T> {
  const {
    autoResize = false,
    minHeight = 26,
    nodeId,
    autoEditNodeId,
    clearAutoEdit,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    readOnly = false,
  } = options

  // Only use state for focus tracking (needed for UI like styling)
  const [isFocused, setIsFocused] = useState(false)

  // Refs
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const isFocusedRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const prevExternalValueRef = useRef(externalValue)

  // Get current value from DOM
  const getDOMValue = useCallback((): T => {
    if (inputRef.current) {
      return inputRef.current.value as T
    }
    return (externalValue ?? '') as T
  }, [externalValue])

  // Set DOM value directly (no React re-render)
  const setDOMValue = useCallback((value: T) => {
    if (inputRef.current) {
      inputRef.current.value = value as string
    }
  }, [])

  // Auto-resize textarea based on content
  const triggerResize = useCallback(() => {
    if (!autoResize || !inputRef.current) return
    const el = inputRef.current
    if (el.tagName !== 'TEXTAREA') return

    el.style.height = 'auto'
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`
  }, [autoResize, minHeight])

  // Sync external value to DOM when it changes (only if not focused)
  useEffect(() => {
    const externalChanged = externalValue !== prevExternalValueRef.current
    prevExternalValueRef.current = externalValue

    if (!externalChanged) return

    // Don't overwrite user's typing
    const isInputFocused =
      isFocusedRef.current || (inputRef.current && document.activeElement === inputRef.current)

    if (isInputFocused) return

    // Check if DOM already has this value
    const currentDOMValue = inputRef.current?.value
    if (currentDOMValue === externalValue) return

    // Update DOM directly
    setDOMValue((externalValue ?? '') as T)
    triggerResize()
  }, [externalValue, setDOMValue, triggerResize])

  // Set initial value on mount
  useEffect(() => {
    if (inputRef.current && externalValue !== undefined) {
      inputRef.current.value = externalValue as string
      triggerResize()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only run on mount
  }, [])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Auto-focus for new nodes (skipped in read-only mode)
  useEffect(() => {
    if (readOnly) return

    const shouldAutoFocus = nodeId ? autoEditNodeId === nodeId : !!autoEditNodeId

    if (shouldAutoFocus && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
      setIsFocused(true)
      isFocusedRef.current = true
      clearAutoEdit?.()
    }
  }, [autoEditNodeId, nodeId, clearAutoEdit, readOnly])

  // Handle value changes - NO React state update, just debounce onChange
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (readOnly) return

      const newValue = e.target.value as T

      // Auto-resize if needed
      if (autoResize) {
        triggerResize()
      }

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Debounce the onChange call
      debounceTimerRef.current = setTimeout(() => {
        onChangeRef.current(newValue)
      }, debounceMs)
    },
    [debounceMs, autoResize, triggerResize, readOnly]
  )

  // Track focus state
  const handleFocus = useCallback(() => {
    if (readOnly) return
    isFocusedRef.current = true
    setIsFocused(true)
  }, [readOnly])

  // Handle blur - flush pending changes
  const handleBlur = useCallback(() => {
    if (readOnly) return

    isFocusedRef.current = false
    setIsFocused(false)

    // Flush any pending debounced onChange immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    // Always send the current value on blur
    const currentValue = getDOMValue()
    onChangeRef.current(currentValue)
  }, [getDOMValue, readOnly])

  // Compute isEmpty from DOM value
  const currentValue = getDOMValue()
  const isEmpty =
    typeof currentValue === 'string' ? currentValue.trim() === '' : currentValue.length === 0

  return {
    value: currentValue,
    isFocused,
    inputRef,
    handleChange,
    handleFocus,
    handleBlur,
    setValue: setDOMValue,
    triggerResize,
    isEmpty,
  }
}
